// ==============================================================================================
//  APPLICATION AUTHORSHIP & RIGHTS NOTICE
//
//  Developed by: Gareth Morgan
//  Brand & Product: BuildMyChecks.co.uk
//  Ownership: Sole author and rights holder
//
//  ----------------------------------------------------------------------------------------------
//  NOTICE:
//  - This application and its related systems were designed, developed, and built solely
//    by Gareth Morgan.
//  - Gareth Morgan retains full authorship, intellectual property, and creative rights to
//    all Build My Checks branding, technology, source code, architecture, and design.
//  - No ownership, license, or implied rights are granted to any third party unless
//    explicitly agreed in writing by Gareth Morgan.
//  - Gareth Morgan is NOT responsible for future maintenance, updates, security patching,
//    or operational upkeep if this application is deployed, copied, or modified without
//    his written consent.
//  - Gareth Morgan reserves the right to:
//       • Withdraw or disable access to the application or related services
//       • Remove hosted, cloud-based, or linked services under his control
//       • Revoke access to Build My Checks–related assets, code, APIs, or integrations
//
//  © 2025 Gareth Morgan — BuildMyChecks.co.uk
//  Unauthorized use, alteration, copying, or redistribution without written consent
//  is strictly prohibited.
//
//  ----------------------------------------------------------------------------------------------
//  Build My Checks – ASCII Logo Signature
//  (Symbolic representation of BuildMyChecks.co.uk identity)
//
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................:-==++-.........:-:.............................................
// ...................................-*******:.......=*****+-.........................................
// ...................................=*******+:.....+*******=.........................................
// .........................::........+**********************=.........................................
// .......................-+***+-.:-++***********************=:.......:................................
// .....................-+*************************************+-..=+**+-..............................
// .....................:+*************+=-.............-++***************+:............................
// ......................:+********+=:.....................:=*************=............................
// .......................=******+:...........................-+********+-.............................
// ......................-*****+:...............................-+*****+-..............................
// ................:::::+****+-...................................=*****+..............................
// ...............+-......:=+-........+=.*%=......##:.:::.....:::..-*****=.............................
// ..............=*-*@%%%%@*.........-#*:#@=......@%::@@@*...*@@#...-+++++**++=-.......................
// .............:**-*@*..=@%.=#+..*#=-%#:#@=.=#%#+@%::@%%%-.=@%@#-##:..##:+*****:......................
// ..............-+-*@@%%@%+.+@*..#@==%#:#@==@%-:+@%::@#-@#:%#+%#.+@#.*@+-******=......................
// .................*@*..:#@++@*..#@==%#:#@=+@#...@%::@#.+@%%-=%#..%@#%#:+******+......................
// .................*@%##%@#.-%@%%@@==%#:#@=:#@%#%%%::@#.:#@*.=%#...%@%:=****+-:.......................
// ..................::........::.........:....::.....:....:......:-#@--***+:..........................
// ................:++:+@@@@@*.:@#:.....................#%=......:#%#-.+***+:..........................
// .............-+**+:#@*:.:+%+:@#-=+-...:=+=-....-+=-..#%=.:=-..-++-..+***+:..........................
// .............+***=-@%.......:@@#+#@#.+@#-+%#:+@%++@%:#%+*@#::%#--*-:*****+-.........................
// .............-***=:@%.......:@%:.-%%:%@%%%%%+#@=.....#@@@%:..#%@@%-=**+++++*+.......................
// ..............****-+%@*=+%@*:@%:.-%%.*@*-=*=.*@%==%#:#@+=@%=:++-+@*-+--===:+=.......................
// .................::::=***=:..++..:++..-+**=...:+**+:.=+-.:++:-*#*=::-:+***=::-++:...................
// ....................-****+=............................*#......+*****************=..................
// .....................=*****=.......+##+.:+##=....*+.-#-*#.**:..:+************++*=...................
// .....................+******+-....*%..:.%*.:%+..:%*.=%=*%%*..:--***********+:..-+:..................
// ....................+*********=:%=+%*+%-*%+*%-=#-#%*%@=*#:%*:+****+++****+=...=*****-...............
// ...................=**********+--::.::....::...:..::.:.:-::-:=***+:..=+*+:..:+******-...............
// ....................-******++*******+=-:............-=++****+=+***+:...:...=******+-:...............
// ......................-++-....=+***************************+=+-=****+:...-+******-..................
// ................................=**********************++-:....=******=:+********=..................
// ................................=**********************=......:+*****************+:.................
// ................................+*******=......+*******=........-+=.:=+****+-.-+-...................
// ................................:-=+***-........+******=..............=***+.........................
// .................................................--::...............................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
//
// ==============================================================================================

// src/pages/Checkout.jsx — Stripe Managed Payments (Global Payments)
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PRICE_PER_USER, BUNDLE_PRICE_PER_USER } from '../../stripe';
import { auth, db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [userCount, setUserCount] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState(null);
  const [bundleEligible, setBundleEligible] = useState(false);

  const pricePerUser = bundleEligible ? BUNDLE_PRICE_PER_USER : PRICE_PER_USER;
  const totalAmount = userCount ? userCount * pricePerUser : 0;
  const formatPrice = (value) => `£${value.toFixed(2)}`;

  useEffect(() => {
    const loadCompanyData = async () => {
      try {
        let cId = location.state?.companyId;

        if (!cId) {
          const user = auth.currentUser;
          if (!user) { navigate('/login'); return; }
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          cId = userDoc.data()?.companyId;
        }

        if (!cId) { navigate('/setup'); return; }

        const companyDoc = await getDoc(doc(db, 'companies', cId));
        if (!companyDoc.exists()) { navigate('/setup'); return; }

        const companyData = companyDoc.data();
        setUserCount(companyData.userLimit || location.state?.userCount || 1);
        setCompanyId(cId);

        // Check if company has an active BuildMyShifts subscription for bundle pricing
        const bmsStatus = companyData.shiftsSubscriptionStatus;
        if (['active', 'trialing'].includes(bmsStatus)) {
          setBundleEligible(true);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading company data:', err);
        navigate('/setup');
      }
    };
    loadCompanyData();
  }, [location.state, navigate]);

  const handleCheckout = async () => {
    setRedirecting(true);
    setError(null);

    try {
      const functions = getFunctions();
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

      const result = await createCheckoutSession({
        userCount: userCount,
        companyId: companyId,
        isBundle: bundleEligible,
      });

      const { url } = result.data;

      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setRedirecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 flex items-center justify-center text-white">
        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl px-6 py-5 shadow-2xl text-sm text-white/80">
          Loading your checkout...
        </div>
      </div>
    );
  }

  if (!userCount || !companyId) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8 text-white">
          <p className="text-xs uppercase tracking-[0.2em] text-white/60">Checkout</p>
          <h1 className="text-3xl sm:text-4xl font-bold">Confirm your subscription</h1>
          <p className="text-white/60 mt-2">You can change seat counts anytime in Billing.</p>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 shadow-2xl text-white">
          <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Your plan</p>
          <h2 className="text-3xl font-bold mb-1">Team subscription</h2>
          <p className="text-white/70 text-sm mb-6">Flexible monthly billing. Adjust seats anytime.</p>

          <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-5 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-emerald-50">Users</span>
              <span className="text-xl font-semibold text-emerald-100">{userCount}</span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-emerald-50">Per user</span>
              <span className="text-lg text-emerald-50">{formatPrice(pricePerUser)}/mo</span>
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-emerald-400/20">
              <span className="text-sm text-emerald-50">Total monthly</span>
              <span className="text-2xl font-semibold text-emerald-50">{formatPrice(totalAmount)}/mo</span>
            </div>
            {bundleEligible && (
              <div className="mt-3 bg-green-500/20 text-green-300 text-sm px-3 py-1.5 rounded-lg">
                50% bundle discount applied — you have an active BuildMyShifts subscription!
              </div>
            )}
          </div>

          <div className="space-y-2 text-sm text-white/70 mb-8">
            <div className="flex items-center gap-2"><span className="text-lg">🌍</span>Global payment methods supported</div>
            <div className="flex items-center gap-2"><span className="text-lg">🔒</span>Secure Stripe checkout</div>
            <div className="flex items-center gap-2"><span className="text-lg">📊</span>Automatic tax calculation</div>
            <div className="flex items-center gap-2"><span className="text-lg">↺</span>Change seats any time</div>
            <div className="flex items-center gap-2"><span className="text-lg">📄</span>VAT invoices available</div>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-xl border border-red-400/30 bg-red-500/15 text-sm text-red-50">
              {error}
            </div>
          )}

          <button
            onClick={handleCheckout}
            disabled={redirecting}
            className={`w-full py-4 px-4 rounded-xl font-semibold text-white text-lg transition-all shadow-lg ${
              redirecting
                ? 'bg-white/10 cursor-not-allowed'
                : 'bg-gradient-to-r from-fuchsia-500 via-purple-500 to-blue-500 hover:shadow-xl hover:-translate-y-0.5'
            }`}
          >
            {redirecting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Redirecting to Stripe...
              </span>
            ) : (
              `Proceed to payment — ${formatPrice(totalAmount)}/mo`
            )}
          </button>

          <p className="text-xs text-white/50 text-center mt-4">
            You'll be redirected to Stripe's secure checkout page to complete your payment.
          </p>

          <div className="mt-6 pt-4 border-t border-white/10 flex justify-center">
            <button
              onClick={() => navigate('/subscription')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/5 text-white/70 hover:bg-white/10 text-sm transition-all"
            >
              ← Back to subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------------------------
//  Build My Checks (c) 2025 Gareth Morgan - BuildMyChecks.co.uk
//  Solely designed, developed, and maintained by Gareth Morgan.
//  All intellectual property rights reserved.
//  No liability is accepted for unauthorised use, modification, deployment, or maintenance.
// ----------------------------------------------------------------------------------------------
